import { useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizeApiDetail } from '../../i18n/apiError'
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { leadsAPI } from './api'
import type { LeadRow, UnderenhetMatch } from './api'
import UnderenhetMatchNotice from './UnderenhetMatchNotice'
import Pagination from './Pagination'
import { formatNorwegianDate } from '../../utils/dateUtils'
import EierhistorikkKnapp from './eierhistorikk/EierhistorikkKnapp'
import { useKanSeEierhistorikk } from './eierhistorikk/tilgang'
import { useBookmarks } from './useBookmarks'
import BookmarkStar from './BookmarkStar'
import { useLatest } from '../Bruktbil/useLatest'

type SortCol = 'lead_score' | 'start_date'
type Sort = { col: SortCol; dir: 'asc' | 'desc' } | null

interface Props {
  page: number
  onPageChange: (page: number) => void
  markedsomradeIds?: string
  search: string
  naceFilter: string
  minScore?: number
  categoryFilter?: string
  mineNaceKoder?: boolean
  vehicleClass?: string
}

const PAGE_SIZE = 20

function SortIcon({ col, sort }: { col: SortCol; sort: Sort }) {
  if (!sort || sort.col !== col) return <ChevronsUpDown className="w-4 h-4 inline ml-1 opacity-40" />
  return sort.dir === 'asc'
    ? <ArrowUp className="w-4 h-4 inline ml-1" />
    : <ArrowDown className="w-4 h-4 inline ml-1" />
}

const SORT_COLS: SortCol[] = ['lead_score', 'start_date']

/** Sort as the `ls` URL param ("col:dir", or "none" once cleared), so it
 *  survives a trip to the bedrift page the way the filters and page do. */
function parseSort(raw: string | null): Sort {
  if (raw === 'none') return null
  const [col, dir] = (raw ?? '').split(':')
  if (SORT_COLS.includes(col as SortCol) && (dir === 'asc' || dir === 'desc')) return { col: col as SortCol, dir }
  return { col: 'lead_score', dir: 'desc' }
}

const VEHICLE_CLASS_LABEL: Record<string, 'common.personbil' | 'common.varebil'> = { personbil: 'common.personbil', varebil: 'common.varebil' }

export default function LeadsTab({ page, onPageChange, markedsomradeIds, search, naceFilter: _nace, minScore, categoryFilter, mineNaceKoder, vehicleClass }: Props) {
  const { t } = useTranslation('leads')
  const kanSeHistorikk = useKanSeEierhistorikk()
  const [rows, setRows] = useState<LeadRow[]>([])
  const [total, setTotal] = useState(0)
  const [totalIsCapped, setTotalIsCapped] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [underenhetMatch, setUnderenhetMatch] = useState<UnderenhetMatch | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const sortParam = searchParams.get('ls')
  const sort = parseSort(sortParam)
  const { isBookmarked, toggle: toggleBookmark } = useBookmarks()

  // No page reset here: LeadsPage.applyFilters already drops the page when a
  // filter changes, and an effect on mount set it back to 1 every time the
  // user returned from a bedrift page (#627 AC4).

  const sortBy = sort ? sort.col : 'lead_score'

  // useLatest: a superseded answer must not replace a newer one (#901).
  useLatest(alive => {
    setLoading(true)
    setError(null)
    leadsAPI.getLeads({
      markedsomrade_ids: markedsomradeIds,
      org_nr: search || undefined,
      category_code: categoryFilter || undefined,
      vehicle_class: vehicleClass || undefined,
      min_score: minScore,
      mine_nace_koder: mineNaceKoder || undefined,
      sort_by: sortBy,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    })
      .then(res => {
        if (!alive()) return
        setRows(res.data.items)
        setTotal(res.data.total)
        setTotalIsCapped(res.data.total_is_capped)
        setHasMore(res.data.has_more)
        setUnderenhetMatch(res.data.underenhet_match ?? null)
      })
      .catch(err => { if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message) })
      .finally(() => { if (alive()) setLoading(false) })
  }, [markedsomradeIds, search, minScore, categoryFilter, mineNaceKoder, vehicleClass, sortBy, page])

  function handleSort(col: SortCol) {
    const next: Sort = sort?.col === col ? (sort.dir === 'desc' ? { col, dir: 'asc' } : null) : { col, dir: 'desc' }
    setSearchParams(prev => {
      const p = new URLSearchParams(prev)
      if (next?.col === 'lead_score' && next.dir === 'desc') p.delete('ls')
      else p.set('ls', next ? `${next.col}:${next.dir}` : 'none')
      return p
    }, { replace: true })
  }


  if (error) {
    return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>
  }

  return (
    <div className="card p-6">
      {loading && <div className="animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm mb-4" />}

      {!loading && (
        <>
          <UnderenhetMatchNotice match={underenhetMatch} />
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th className="w-8" aria-label={t('common.mineLeads')} />
                  <th className="text-left whitespace-nowrap">{t('common.kjennemerke')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.bedrift')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.kategori')}</th>
                  <th className="text-right whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleSort('lead_score')}>
                    {t('common.score')} <SortIcon col="lead_score" sort={sort} />
                  </th>
                  <th className="text-left whitespace-nowrap">{t('common.merke')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.modell')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.type')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.firstReg')}</th>
                  <th className="text-left whitespace-nowrap">{t('common.regPaEier')}</th>
                  <th className="text-left whitespace-nowrap cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                      onClick={() => handleSort('start_date')}>
                    {t('leadsTab.fra')} <SortIcon col="start_date" sort={sort} />
                  </th>
                  {/* No column at all without the role, rather than an empty one. */}
                  {kanSeHistorikk && <th className="text-center whitespace-nowrap">{t('common.historikk')}</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id} className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}>
                    <td className="text-center">
                      <BookmarkStar active={isBookmarked(row.org_nr)} onToggle={() => toggleBookmark(row.org_nr)} />
                    </td>
                    <td className="font-mono text-xs text-primary-600 dark:text-primary-400">{row.kjennemerke ?? '\u2014'}</td>
                    <td className="font-bold">
                      <button
                        onClick={() => navigate(`/leads/bedrift/${row.org_nr}`, { state: { from: location.pathname + location.search } })}
                        className="text-left hover:underline hover:text-primary-600 dark:hover:text-primary-400"
                        title={t('leadsTab.openCompany')}
                      >
                        {row.org_navn ?? row.org_nr}
                      </button>
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
                        {row.category_name}
                      </span>
                    </td>
                    <td className="text-right font-bold">{row.lead_score}</td>
                    <td className="text-sm">{row.merke ?? '\u2014'}</td>
                    <td className="text-sm">{row.modell ?? '\u2014'}</td>
                    <td className="text-xs">{row.vehicle_class ? (VEHICLE_CLASS_LABEL[row.vehicle_class] ? t(VEHICLE_CLASS_LABEL[row.vehicle_class]) : row.vehicle_class) : '\u2014'}</td>
                    <td className="text-xs text-gray-500">{row.registrert_forste_gang ? formatNorwegianDate(row.registrert_forste_gang) : '\u2014'}</td>
                    <td className="text-xs font-medium">{row.registrert_forste_gang_pa_eier ? formatNorwegianDate(row.registrert_forste_gang_pa_eier) : '\u2014'}</td>
                    <td className="text-xs text-gray-500">{row.start_date ? formatNorwegianDate(row.start_date) : '\u2014'}</td>
                    {kanSeHistorikk && (
                      <td className="text-center">
                        <EierhistorikkKnapp
                          kjennemerke={row.kjennemerke}
                          kuid={row.kuid}
                          registrertPaEier={row.registrert_forste_gang_pa_eier}
                          navaerendeEier={row.org_navn ?? row.org_nr}
                          merke={row.merke}
                          modell={row.modell}
                          registrertForsteGang={row.registrert_forste_gang}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('leadsTab.empty')}</p>
          )}

          <Pagination
            page={page}
            onPageChange={onPageChange}
            total={total}
            totalIsCapped={totalIsCapped}
            hasMore={hasMore}
            pageSize={PAGE_SIZE}
          />
        </>
      )}
    </div>
  )
}
